import React, { useEffect, useMemo } from 'react';
import Select from 'react-select';
import { CountryCode, CountryFlagForCode, CountryName, CountryNameKey } from '@dayetopia/types';
import styled from 'styled-components';
import { CountrySelectStyleVariant, CountryTuple, SelectOption } from '@contracts';
import flags from '@icons/flags';
import { countrySelectStyles } from '@utils/countrySelectStyles';
import { getCurrencyFromCountryCode } from '@utils/currency';
import { sortCountries } from '@utils/helpers';
import { getLocaleByCountryCode, removeLocalesFromPath } from '@utils/language';
import useCurrentLocation from '@utils/useCurrentLocation';
import LinkTranslated from './LinkTranslated';

interface Props {
  selectedCountry: CountryCode | null;
  defaultCountry: CountryCode | null;
  setSelectedCountry: (country: CountryCode) => void;
  setDefaultCountry?: (country: CountryCode) => void;
  styleVariant?: CountrySelectStyleVariant;
}

const CountrySwitcher: React.FC<Props> = ({ selectedCountry, defaultCountry, setSelectedCountry, setDefaultCountry, styleVariant = 'default' }) => {
  const currentLocation = useCurrentLocation();
  const locale = getLocaleByCountryCode(selectedCountry || defaultCountry || CountryCode.GB);

  const options = useMemo(() => {
    return sortCountries(Object.entries(CountryFlagForCode) as CountryTuple[], locale)
      .filter(([countryCode]) => CountryName[countryCode as CountryNameKey])
      .map(([countryCode]) => {
        const FlagIcon = flags[countryCode as CountryCode];

        return {
          value: countryCode,
          label: (
            <OptionLabel>
              <LinkTranslated
                to={removeLocalesFromPath(currentLocation)}
                language={getLocaleByCountryCode(countryCode as CountryCode)}
                hrefCountry={countryCode}
                preventDefault
              >
                <Flag>{FlagIcon && <FlagIcon />}</Flag>
                {CountryName[countryCode as CountryNameKey]} ({getCurrencyFromCountryCode(countryCode as CountryCode)})
              </LinkTranslated>
            </OptionLabel>
          )
        };
      });
  }, [currentLocation, selectedCountry, defaultCountry]);
  const selectedOption = useMemo(
    () => options.find((option) => option.value === (selectedCountry || defaultCountry)),
    [options, selectedCountry, defaultCountry]
  );

  useEffect(() => {
    if (selectedOption && setDefaultCountry) {
      setDefaultCountry(selectedOption.value as CountryCode);
    }
  }, []);

  const handleChange = (option: SelectOption | undefined | null) => {
    if (option?.value) {
      setSelectedCountry(option.value as CountryCode);
    }
  };

  return (
    <Wrapper>
      <Select
        value={selectedOption}
        options={options}
        onChange={handleChange}
        styles={countrySelectStyles(styleVariant)}
        menuPortalTarget={styleVariant === 'default' ? document?.body : undefined}
        isSearchable={false}
      />
    </Wrapper>
  );
};

export default CountrySwitcher;

const Wrapper = styled.div``;

const Flag = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  margin-right: 2px;
`;

const OptionLabel = styled.div`
  a {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    color: inherit;
    text-decoration: none;
    font-family: BrownStd;

    &:hover {
      text-decoration: none;
    }
  }
`;
